import type { Applicant, Application, JobOpening, Offer } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useRoute } from "wouter";
import { OfferTemplateIframe } from "@/components/offer-template-iframe";
import { Card, CardContent } from "@/components/ui/card";
import { renderOfferTemplate } from "@/lib/offer-template-utils";
import { rpcClient } from "@/lib/rpc";

type OfferWithDetails = Offer & {
  hiringManagerName?: string | null;
  application?: Application & {
    applicant?: Applicant;
    jobOpening?: JobOpening;
  };
};

export default function OfferPreview() {
  const [, params] = useRoute("/offer-preview/:token");
  const token = params?.token;

  const {
    data: offer,
    isLoading,
    error,
  } = useQuery<OfferWithDetails>({
    queryKey: ["/rpc/offers/preview", token],
    queryFn: () => rpcClient.offers.getByPreviewToken({ token: token! }),
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-muted-foreground">Carregando carta de oferta...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isForbidden = (error as any)?.status === 403 || (error as any)?.code === "FORBIDDEN";

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-12 text-center">
            <h2 className="text-2xl font-semibold mb-2">
              {isForbidden ? "Acesso negado" : "Oferta não encontrada"}
            </h2>
            <p className="text-muted-foreground">
              {isForbidden
                ? "Você não tem permissão para visualizar esta oferta."
                : "Este link de preview é inválido ou expirou."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const applicant = offer.application?.applicant;
  const jobOpening = offer.application?.jobOpening;

  const renderedTemplate = renderOfferTemplate(offer, {
    candidateName: applicant?.fullName || "Candidato",
    jobTitle: jobOpening?.title || "N/A",
  });

  return (
    <div className="h-full overflow-y-auto">
      <OfferTemplateIframe html={renderedTemplate} />
    </div>
  );
}
